import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Globe } from "lucide-react";
import Logo from "../Logo";
import { NAV, CONTACT } from "../../data/site";
import { useServices } from "../../lib/hooks";

// The closing call to action and the footer form one closing section that fits a single screen
// (customer review p14). All original footer content is kept.
export const Footer = () => {
  const capabilities = useServices().slice(0, 6);
  const link = "text-sm text-white/70 hover:text-[#e60000] transition-colors";
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#0a0a0a]">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="pt-10 lg:pt-12 pb-8 lg:pb-10 border-b border-white/10">
          <p className="overline mb-4">Business · Technology · Transformation</p>
          <Link
            to="/contact#enquiry"
            data-testid="footer-cta-link"
            className="group block font-display font-black tracking-tightest leading-[1] text-3xl sm:text-4xl xl:text-5xl text-white"
          >
            <span className="block">Let's design the next</span>
            <span className="block">
              state of your enterprise
              <span className="text-[#e60000] group-hover:pl-4 transition-all duration-500"> .</span>
            </span>
          </Link>
        </div>

        <div className="py-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-12 lg:col-span-4">
            <Logo variant="lockup" size="footer" testId="footer-regera-logo" className="mb-6" />
            <p className="text-regera-dim text-sm max-w-xs leading-relaxed">
              Technology, solutions, platforms and advisory — leading transformation across enterprises,
              government organisations and emerging ventures.
            </p>
          </div>

          <nav className="md:col-span-5 lg:col-span-3" aria-label="Footer">
            <p className="overline mb-5">Navigate</p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className={link}>{n.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-4 lg:col-span-3">
            <p className="overline mb-5">Capabilities</p>
            <ul className="space-y-3">
              {capabilities.map((c) => (
                <li key={c.slug || c.id}>
                  <Link to={`/services#${c.slug}`} className={link}>{c.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3 lg:col-span-2">
            <p className="overline mb-5">Engage</p>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-[#e60000] mt-0.5 shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors break-all">{CONTACT.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-[#e60000] mt-0.5 shrink-0" />
                <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-white transition-colors">{CONTACT.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="h-4 w-4 text-[#e60000] mt-0.5 shrink-0" />
                <span>{CONTACT.web}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#e60000] mt-0.5 shrink-0" />
                <span>{CONTACT.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} REGERA. All rights reserved.</p>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/admin/login" className="hover:text-white transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
